import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useQuery } from '../../../../utils/hooks/useQuery';
import Api from '../../../../api/api';
import classes from './autoRefillData.module.scss';
import utils from '../../../../utils/utils';
import accountHelpers from '../../../../utils/accountHelpers';
import SelectWithSearch from '../../../_common/Form/Selects/SelectWithSearch/SelectWithSearch';
import { RangeDatePicker } from '../../../_common/Form/DatePickers/RangeDatePicker/RangeDatePicker';
import Loader from '../../../_common/Loader/Loader';
import TableAndPagination from '../../../_common/TableAndPagination/TableAndPagination';

const healthLimit = 50;

const thisMonthStartDay = moment().startOf('month').format('YYYY-MM-DD');
const thisMonthEndDate = moment().format('YYYY-MM-DD');

const collectRequestData = (requestData) => {
  const data = utils.deleteEmptyKeys(requestData);

  if (data.start) {
    if (!data.end) {
      data.end = moment(data.start).add(1, 'day').format('YYYY-MM-DD');
    } else {
      data.end = moment(data.end).format('YYYY-MM-DD');
    }
  }

  return data;
};

const healthListParams = (name) => ({
  page: 1,
  limit: healthLimit,
  name,
  searcherId: accountHelpers.getUser().id,
  userRole: 'Health',
  dashboardActive: true,
});

const tableHeader = [
  {
    name: 'Hub',
    path: 'hubId',
    isSelectable: true,
  },
  {
    name: 'Plan Name',
    path: 'planName',
    isSelectable: true,
  },
  // {
  //   name: 'Total',
  //   path: 'total',
  //   isSelectable: true,
  //   render: (d) => utils.formattingCount(d.total),
  // },
  {
    name: '< 7 Days',
    path: 'lt7',
    isSelectable: true,
    render: (d) => `${utils.formattingCount(d.lt7)}%`,
  },
  // {
  //   name: '< 7 Days Count',
  //   path: 'lt7Count',
  //   isSelectable: true,
  //   render: (d) => utils.formattingCount(d.lt7Count),
  // },
  {
    name: '< 14 Days',
    path: 'lt14',
    isSelectable: true,
    render: (d) => `${utils.formattingCount(d.lt14)}%`,
  },
  // {
  //   name: '< 14 Days Count',
  //   path: 'lt14Count',
  //   isSelectable: true,
  //   render: (d) => utils.formattingCount(d.lt14Count),
  // },
  {
    name: '< 21 Days',
    path: 'lt21',
    isSelectable: true,
    render: (d) => `${utils.formattingCount(d.lt21)}%`,
  },
  // {
  //   name: '< 21 Days Count',
  //   path: 'lt21Count',
  //   isSelectable: true,
  //   render: (d) => utils.formattingCount(d.lt21Count),
  // },
];

const AutoRefillData = () => {
  const isAdmin = accountHelpers.isInRole('Admin');

  const { query, setQuery } = useQuery();

  const firstLoad = useRef(true);
  const searchTimeout = useRef(null);

  const [requestData, setRequestData] = useState({
    healthUserId: query.healthUserId || '',
    healthName: query.healthName || '',
    start: query.start || thisMonthStartDay,
    end: query.end || thisMonthEndDate,
    sortBy: query.sortBy || 'hubId',
    sort: query.sort || 'desc',
    page: query.page || 1,
    limit: query.limit || 15,
  });

  const [autofillStats, setAutofillStats] = useState({ stats: [], totalPage: 1 });

  const [loading, setLoading] = useState(false);

  const [healthList, setHealthList] = useState([]);
  const [healthSearchValue, setHealthSearchValue] = useState(query.healthName || '');
  const [healthSearchLoading, healthSearchLoadingToggle] = useState(false);

  const {
    page, limit, sort, sortBy,
  } = requestData;

  useEffect(() => () => clearTimeout(searchTimeout.current), []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const filters = collectRequestData({ ...requestData });
      setQuery(filters);

      setLoading(true);

      try {
        const { data } = await Api.getDashboardAutofillStats(filters);

        if (cancelled) return;

        setAutofillStats({ stats: data.stats || [], totalPage: data._meta?.pageCount || 1 });
      } catch (e) {
        if (cancelled) return;

        console.warn(e);
      }

      setLoading(false);

      firstLoad.current = false;
    })();

    return () => {
      cancelled = true;
    };
  }, [requestData]);

  const searchHealth = (value) => {
    setHealthSearchValue(value);

    clearTimeout(searchTimeout.current);

    if (!value.trim()) {
      setHealthList([]);
      healthSearchLoadingToggle(false);

      return;
    }

    healthSearchLoadingToggle(true);

    searchTimeout.current = setTimeout(async () => {
      try {
        const { data: { data: { response } } } = await Api.getUserList(healthListParams(value));

        setHealthList(response || []);
      } catch (e) {
        console.warn(e);
      }

      healthSearchLoadingToggle(false);
    }, 500);
  };

  const onChangeHealth = (healthUserId = '', healthName = '') => {
    setHealthSearchValue(healthName || '');

    setRequestData((prev) => ({
      ...prev,
      healthUserId: healthUserId || '',
      healthName: healthName || '',
      page: 1,
    }));
  };

  const onChangeDate = ([start, end = '']) => {
    setRequestData((prev) => ({
      ...prev,
      start,
      end,
      page: 1,
    }));
  };

  const onSortBy = async (sorting) => {
    setRequestData((prev) => ({ ...prev, ...sorting }));
  };

  const onChangePageData = async (path, count) => {
    setRequestData((prev) => (path === 'limit'
      ? { ...prev, limit: count, page: 1 }
      : { ...prev, page: count }));
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.wrapper_header}>
        <div className={classes.date_picker_wrapper}>
          <RangeDatePicker
            currentDate={requestData}
            onChangeDate={onChangeDate}
            showLastYear
          />
        </div>

        {isAdmin && (
          <div className={classes.select_wrapper}>
            <SelectWithSearch
              label="Health Plan"
              searchValue={healthSearchValue}
              options={healthList}
              loading={healthSearchLoading}
              onChange={searchHealth}
              onSelectOption={onChangeHealth}
              optionPathname="name"
              zIndex={30}
            />
          </div>
        )}
      </div>

      <div className={classes.table_wrapper}>
        {firstLoad.current
          ? <Loader className={classes.loader} />

          : (
            <TableAndPagination
              data={autofillStats.stats}
              header={tableHeader}
              loading={loading}
              onChange={onChangePageData}
              sort={sort}
              sortBy={sortBy}
              onSortBy={onSortBy}
              page={+page - 1}
              limit={+limit}
              totalPage={+autofillStats.totalPage}
            />
          )}
      </div>
    </div>
  );
};

export default AutoRefillData;
